import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiRefreshCw, FiMail, FiMapPin, FiSearch, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import '../../styles/CertificatesSidebarPanel.css';
const PAGE_SIZE = 20;

const DATE_FILTERS = [
  { label: 'Today', days: 0 },
  { label: '7D',    days: 7 },
  { label: '30D',   days: 30 },
  { label: '90D',   days: 90 },
];

function getDateRange(days: number): { fromDate: string; toDate: string } {
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const to = new Date();
  if (days === 0) return { fromDate: fmt(to), toDate: fmt(to) };
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { fromDate: fmt(from), toDate: fmt(to) };
}

interface CertHolderRecord {
  databaseId: string;
  certificateHolderName: string;
  certificateName: string;
  createDate: string;
  insuredCommercialName: string;
  insuredFirstName: string;
  insuredLastName: string;
  acordFormNumber: number;
  isSent: boolean;
  type: string;
  eMail: string | null;
  city: string | null;
}

interface Props {
  insuredId?: string;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function trimCertName(name: string): string {
  const match = name.match(/\((\d{2}\/\d{2}\/\d{4})\)/);
  const date = match ? ` · ${match[1]}` : '';
  const acord = name.match(/ACORD\s+(\d+)/i);
  return acord ? `ACORD ${acord[1]}${date}` : name.slice(0, 38);
}

const CertificatesSidebarPanel: React.FC<Props> = ({ insuredId }) => {
  const [records, setRecords] = useState<CertHolderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [activeDays, setActiveDays] = useState(7);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchCerts = useCallback(async (days: number, pageIndex: number, q: string) => {
    const amsToken = localStorage.getItem('amsToken') || '';
    const authToken = localStorage.getItem('authToken') || '';
    const { fromDate, toDate } = getDateRange(days);

    const params = new URLSearchParams({
      skip: String(pageIndex * PAGE_SIZE),
      top: String(PAGE_SIZE),
      fromDate,
      toDate,
    });
    if (insuredId) params.set('insuredId', insuredId);
    if (q.trim()) params.set('search', q.trim());

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${(process.env.REACT_APP_Auth_Url)}/api/ampnowcerts/historicalcertificateholderlist?${params}`,
        {
          headers: {
            NCToken: amsToken,
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRecords(data.value || []);
      setTotal(data['@odata.count'] || 0);
    } catch (e: any) {
      setError('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  }, [insuredId]);

  // Fetch when days or page changes (search triggers separately via debounce)
  useEffect(() => {
    fetchCerts(activeDays, page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDays, page]);

  // Debounced search — reset to page 0
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setSearch(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      fetchCerts(activeDays, 0, q);
    }, 400);
  };

  const clearSearch = () => {
    setSearch('');
    setPage(0);
    fetchCerts(activeDays, 0, '');
    searchRef.current?.focus();
  };

  const handleDayChange = (days: number) => {
    setActiveDays(days);
    setPage(0);
  };

  const goPage = (p: number) => setPage(p);

  return (
    <div className="csp">
      {/* Sub-header: date filters + count + refresh */}
      <div className="csp__subheader">
        <div className="csp__date-filters">
          {DATE_FILTERS.map(f => (
            <button
              key={f.days}
              className={`csp__date-chip${activeDays === f.days ? ' csp__date-chip--active' : ''}`}
              onClick={() => handleDayChange(f.days)}
              disabled={loading}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="csp__subheader-right">
          {total > 0 && <span className="csp__badge">{total.toLocaleString()}</span>}
          <button className="csp__refresh" onClick={() => fetchCerts(activeDays, page, search)} title="Refresh" disabled={loading}>
            <FiRefreshCw size={12} className={loading ? 'csp__spin' : ''} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="csp__search-wrap">
        <FiSearch size={12} className="csp__search-icon" />
        <input
          ref={searchRef}
          className="csp__search"
          placeholder="Search sent certificates…"
          value={search}
          onChange={handleSearchChange}
          disabled={loading && records.length === 0}
        />
        {search && (
          <button className="csp__search-clear" onClick={clearSearch} title="Clear">
            <FiX size={11} />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="csp__body">
        {loading && records.length === 0 && (
          <div className="csp__loading">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="csp__skeleton" />
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="csp__error">
            <span>{error}</span>
            <button className="csp__retry" onClick={() => fetchCerts(activeDays, page, search)}>Retry</button>
          </div>
        )}

        {!loading && !error && records.length === 0 && (
          <div className="csp__empty">
            <FiFileText size={28} />
            <span>{search ? 'No results found' : 'No certificates found'}</span>
          </div>
        )}

        {records.map((rec, i) => (
          <motion.div
            key={rec.databaseId}
            className="csp__card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03, duration: 0.2 }}
          >
            {/* Top row: holder + sent badge */}
            <div className="csp__card-top">
              <span className="csp__holder">{rec.certificateHolderName}</span>
              {rec.isSent && <span className="csp__sent-badge">Sent</span>}
            </div>

            {/* Cert name */}
            <div className="csp__cert-name">
              <FiFileText size={11} className="csp__cert-icon" />
              {trimCertName(rec.certificateName)}
            </div>

            {/* Insured */}
            <div className="csp__meta">
              <span className="csp__meta-label">Insured</span>
              <span className="csp__meta-value">
                {rec.insuredCommercialName || `${rec.insuredFirstName} ${rec.insuredLastName}`}
              </span>
            </div>

            {/* City + email row */}
            <div className="csp__card-footer">
              {rec.city && (
                <span className="csp__location">
                  <FiMapPin size={10} />
                  {rec.city}
                </span>
              )}
              {rec.eMail && (
                <span className="csp__email">
                  <FiMail size={10} />
                  {rec.eMail}
                </span>
              )}
              <span className="csp__date">{formatDate(rec.createDate)}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Pagination */}
      {!error && totalPages > 1 && (
        <div className="csp__pagination">
          <button
            className="csp__page-btn"
            onClick={() => goPage(page - 1)}
            disabled={page === 0 || loading}
            title="Previous"
          >
            <FiChevronLeft size={13} />
          </button>

          <span className="csp__page-info">
            {page + 1} / {totalPages}
          </span>

          <button
            className="csp__page-btn"
            onClick={() => goPage(page + 1)}
            disabled={page >= totalPages - 1 || loading}
            title="Next"
          >
            <FiChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CertificatesSidebarPanel;

